import React, { useEffect, useState } from 'react';
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Stack, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { fmtMoney, todayIso } from '../../utils/format';

/** Records one payment (a prepayment, an instalment or the rest); the amount starts as what is still owed. */
export default function PaymentDialog({ open, job, onClose, onConfirm }) {
    const { t } = useTranslation();
    const [amount, setAmount] = useState('');
    const [date, setDate] = useState(todayIso());
    const [note, setNote] = useState('');
    const [errors, setErrors] = useState({});
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        const owed = job?.outstanding != null && Number(job.outstanding) > 0 ? String(job.outstanding) : '';
        setAmount(owed); setDate(todayIso()); setNote(''); setErrors({});
    }, [open, job]);

    // More than what is still owed: allowed (e.g. extra work paid on the spot), but say so.
    const owed = job?.outstanding != null ? Math.max(Number(job.outstanding), 0) : null;
    const over = owed != null && amount !== '' && Number(amount) > owed ? Number(amount) - owed : 0;

    const confirm = async () => {
        const v = {};
        if (amount === '' || !(Number(amount) > 0)) v.amount = t('payment.errors.amount');
        if (!date) v.date = t('validation.required');
        setErrors(v);
        if (Object.keys(v).length) return;
        setBusy(true);
        try { await onConfirm({ amount: Number(amount), paidOn: date, note: note.trim() || null }); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontWeight: 700 }}>{t('payment.addTitle')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField size="small" type="number" label={t('payment.amount')} value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus
                               error={!!errors.amount} helperText={errors.amount}
                               slotProps={{ input: { startAdornment: <InputAdornment position="start">€</InputAdornment> }, htmlInput: { min: 0, step: '0.01' } }} />
                    {over > 0 && (
                        <Alert severity="warning" sx={{ borderRadius: '10px', py: 0.25 }}>
                            {t('payment.overWarning', { owed: fmtMoney(owed), over: fmtMoney(Math.round(over * 100) / 100) })}
                        </Alert>
                    )}
                    <TextField size="small" type="date" label={t('payment.date')} value={date} onChange={(e) => setDate(e.target.value)}
                               error={!!errors.date} helperText={errors.date} slotProps={{ inputLabel: { shrink: true } }} />
                    <TextField size="small" label={t('payment.note')} placeholder={t('payment.notePlaceholder')} value={note}
                               onChange={(e) => setNote(e.target.value)} slotProps={{ htmlInput: { maxLength: 300 } }} />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={onClose} disabled={busy} sx={{ color: '#64748B' }}>{t('common.cancel')}</Button>
                <Button variant="contained" color="success" onClick={confirm} disabled={busy} disableElevation>
                    {busy ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('payment.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
